// Export report and share links in the property page's Pack panel (Neil, 10 Oct 2026: "export
// report button that generates pdf ... share link ... and an option to send link to email too").
// Completed orders only. Export renders the page as the pack PDF in the background (100-api
// functions/reports → 120-pack), so it polls until it's ready, then downloads it. A share link sends
// the report as it is when the link is made: 7 days, revocable, each download counted; copy it or
// email it from here.
import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, Download, FileText, Link2, Loader2, Mail, RefreshCw, Trash2 } from "lucide-react";
import { useApi, type ReportState, type ShareLink } from "../api";

const when = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";
const day = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

// How often, and for how long, to look again while a report renders
const POLL_MS = 4000;
const POLL_FOR_MS = 4 * 60 * 1000;

export function ReportPanel({ orderId, initial, pricingConfirmed = true }: { orderId: string; initial?: ReportState; pricingConfirmed?: boolean }) {
  const api = useApi();
  const [report, setReport] = useState<ReportState>(initial ?? { status: "none" });
  const [shares, setShares] = useState<ShareLink[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [emailFor, setEmailFor] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const downloadWhenReady = useRef(false);

  const fail = (e: unknown) => setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");

  /** Fetch a fresh 5-minute link and start the download. */
  const download = useCallback(async () => {
    setBusy("download");
    setError(null);
    try {
      const { url } = await api.getReportLink(orderId);
      window.location.assign(url);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }, [api, orderId]);

  // The live share links
  useEffect(() => {
    if (report.status !== "ready") return;
    api
      .getShares(orderId)
      .then(setShares)
      .catch(() => undefined);
  }, [api, orderId, report.status]);

  // While it renders, look again every few seconds; download it when it's ready, if we started it
  useEffect(() => {
    if (report.status !== "generating") return;
    const started = Date.now();
    const t = window.setInterval(() => {
      if (Date.now() - started > POLL_FOR_MS) {
        window.clearInterval(t);
        setReport({ status: "failed", error: "It's taking longer than expected. Please try again." });
        return;
      }
      api
        .getReport(orderId)
        .then((r) => {
          setReport(r);
          if (r.status === "ready" && downloadWhenReady.current) {
            downloadWhenReady.current = false;
            void download();
          }
        })
        .catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [api, orderId, report.status, download]);

  const exportReport = async () => {
    setBusy("export");
    setError(null);
    try {
      downloadWhenReady.current = true;
      setReport(await api.startReport(orderId));
    } catch (e) {
      downloadWhenReady.current = false;
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const copy = async (s: ShareLink) => {
    try {
      await navigator.clipboard.writeText(s.url);
      setCopied(s.shareId);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setError("Your browser blocked copying: select the link and copy it.");
    }
  };

  const newLink = async () => {
    setBusy("share");
    setError(null);
    setNotice(null);
    try {
      const { share } = await api.createShare(orderId);
      setShares((list) => [share, ...list]);
      await copy(share);
      setNotice("Link created and copied. It works for 7 days.");
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const send = async (s: ShareLink) => {
    setBusy(`email-${s.shareId}`);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.emailShare(orderId, s.shareId, email.trim());
      setShares((list) => list.map((x) => (x.shareId === s.shareId ? updated : x)));
      setNotice(`Sent to ${email.trim()}.`);
      setEmail("");
      setEmailFor(null);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (s: ShareLink) => {
    if (!window.confirm("Revoke this link? Anyone who has it won't be able to download the Solar Opportunity Report.")) return;
    setBusy(`revoke-${s.shareId}`);
    setError(null);
    try {
      await api.revokeShare(orderId, s.shareId);
      setShares((list) => list.filter((x) => x.shareId !== s.shareId));
      setNotice("Link revoked.");
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const generating = report.status === "generating";
  // Every figure in the report uses the pricing model: it waits until the customer has confirmed it
  if (!pricingConfirmed && !generating) {
    return (
      <div className="rp">
        <p className="building-small rp-error">
          Confirm the <a href="#pricing-model">pricing model</a> first: every figure in the report uses it.
        </p>
      </div>
    );
  }
  return (
    <div className="rp">
      {/* Export: made on other prices than the figures now use → regenerate, then download */}
      {report.status === "ready" && report.outdated ? (
        <>
          <p className="building-small rp-error">
            This copy was made on {report.pricingModelName ? `${report.pricingModelName}${report.pricingVersion ? ` v${report.pricingVersion}` : ""}` : "other prices"},
            but the figures now use other prices. Regenerate it to match.
          </p>
          <button type="button" className="btn btn-primary building-cta" onClick={exportReport} disabled={busy === "export"}>
            <RefreshCw size={16} aria-hidden="true" /> Regenerate and download (PDF)
          </button>
        </>
      ) : report.status === "ready" ? (
        <>
          <button type="button" className="btn btn-primary building-cta" onClick={download} disabled={busy === "download"}>
            <Download size={16} aria-hidden="true" /> {busy === "download" ? "Preparing…" : "Download (PDF)"}
          </button>
          <p className="building-small muted rp-meta">
            <FileText size={13} aria-hidden="true" /> Generated {when(report.generatedAt)} ·{" "}
            <button type="button" className="rp-linkbtn" onClick={exportReport} disabled={!!busy}>
              <RefreshCw size={12} aria-hidden="true" /> Regenerate
            </button>
          </p>
        </>
      ) : (
        <button type="button" className="btn btn-primary building-cta" onClick={exportReport} disabled={generating || busy === "export"}>
          {generating ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <FileText size={16} aria-hidden="true" />}{" "}
          {generating ? "Generating…" : "Export (PDF)"}
        </button>
      )}
      {generating && <p className="building-small muted">This takes about half a minute. It downloads when it's ready.</p>}
      {report.status === "failed" && <p className="building-small rp-error">{report.error ?? "The Solar Opportunity Report couldn't be generated. Please try again."}</p>}

      {/* Share links: the latest report, 7 days, revocable */}
      {report.status === "ready" && (
        <div className="rp-share">
          <div className="rp-share-head">
            <b>Share</b>
            <button type="button" className="btn btn-secondary rp-new" onClick={newLink} disabled={busy === "share"}>
              <Link2 size={15} aria-hidden="true" /> {busy === "share" ? "Creating…" : "New share link"}
            </button>
          </div>
          <p className="building-small muted">Anyone with a link can download the Solar Opportunity Report as it was when the link was made, for 7 days. You can revoke it at any time.</p>
          {shares.map((s) => (
            <div key={s.shareId} className="rp-link">
              <input className="rp-url" readOnly value={s.url} onFocus={(e) => e.currentTarget.select()} aria-label="Share link" />
              <div className="rp-actions">
                <button type="button" className="rp-icon" onClick={() => copy(s)} title="Copy link" aria-label="Copy link">
                  {copied === s.shareId ? <Check size={15} /> : <Copy size={15} />}
                </button>
                <button
                  type="button"
                  className="rp-icon"
                  onClick={() => setEmailFor(emailFor === s.shareId ? null : s.shareId)}
                  title="Email link"
                  aria-label="Email link"
                >
                  <Mail size={15} />
                </button>
                <button type="button" className="rp-icon is-danger" onClick={() => revoke(s)} title="Revoke link" aria-label="Revoke link">
                  <Trash2 size={15} />
                </button>
              </div>
              <p className="rp-linkmeta muted">
                Until {day(s.expiresAt)} · {s.downloads ?? 0} download{s.downloads === 1 ? "" : "s"}
                {s.sentTo?.length ? ` · sent to ${s.sentTo.join(", ")}` : ""}
              </p>
              {emailFor === s.shareId && (
                <form
                  className="rp-email"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void send(s);
                  }}
                >
                  <input type="email" required placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email address" />
                  <button type="submit" className="btn btn-secondary" disabled={busy === `email-${s.shareId}`}>
                    {busy === `email-${s.shareId}` ? "Sending…" : "Send"}
                  </button>
                </form>
              )}
            </div>
          ))}
        </div>
      )}
      {notice && <p className="building-small rp-notice">{notice}</p>}
      {error && <p className="building-small rp-error">{error}</p>}
    </div>
  );
}
